import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { RootState } from 'src/reducers';
import { Partner } from 'src/reducers/partner/types';

import { displayPartner } from 'Utilities/helpers';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';

import { ERROR_PARTNERS_INITIATE } from 'Constants/errorTypes';
import { PARTNER_TYPES } from 'Constants/partnerTypes';

import LibreAppWrapper from 'Containers/LibreAppWrapper';

import LoadingButton from 'Components/loading/LoadingButton';

const EMPTY_OBJ = {};

const mapState = ({
  nav: { location },
  partner: { connected, partners, pendingConnections },
}: RootState) => {
  const id = location.split('/')[2];
  const errorPath = location.toLowerCase().endsWith('/error');
  const isConnected = !!connected.find((v) => v.clientId === id);

  return {
    partner: partners[id] || EMPTY_OBJ,
    partners,
    isConnected,
    pendingConnections: pendingConnections,
    connectedOriginal: connected,
    errorPath: errorPath,
  };
};

const connector = connect(mapState);

const cancel = () => {
  mediator.publish('router:redirect', '/otherConnections');
};

const connectInbound = (partner: Partner) => {
  mediator.request('partner:initiateInboundConnection', { partnerId: partner.id }).catch((err) => {
    mediator.publish('router:navigate', '/');

    mediator.publish('error:display', {
      error: {
        code: err.status,
        type: ERROR_PARTNERS_INITIATE,
      },
    });
  });
};

type Props = ConnectedProps<typeof connector>;

const InboundPartner: React.FC<React.PropsWithChildren<Props>> = ({
  partner,
  partners,
  isConnected,
  pendingConnections,
  connectedOriginal,
  errorPath,
}) => {
  // We shouldn't be here if this is an invalid partner, or the partner shouldn't be displayed
  useEffect(() => {
    if (
      !partner ||
      !partner.id ||
      partner.type !== PARTNER_TYPES.INBOUND ||
      !displayPartner(partner) ||
      isConnected
    ) {
      mediator.publish('partner:navigateOtherConnections', {
        partners,
        connected: connectedOriginal,
        pending: pendingConnections,
      });
    }
  }, [partner, partners, isConnected, connectedOriginal, pendingConnections]);

  useEffect(() => {
    if (errorPath) {
      mediator.publish('partner:errorLoadingModal');
    }
  }, [errorPath]);

  const [running, setRunning] = useState(false);

  // Setup an interval that checks every 200ms if a full second has passed, and if so unset the running ID
  // This will only ever happen when navigating back from somewhere else (i.e. a connected partner website)
  // Needed for managing the loading buttons on iOS
  useEffect(() => {
    let interval = 0;
    let prev = new Date().getTime();

    if (running) {
      interval = window.setInterval(() => {
        const now = new Date().getTime();
        const diff = now - prev - 200;

        if (diff > 1000) {
          setRunning(false);
        }

        prev = now;
      }, 200);
    }

    return () => {
      window.clearInterval(interval);
    };
  }, [running]);

  const inboundApp = {
    id: 'inbound',
    logo: '',
    transGroupKey: 'OtherConnections',
  };

  return (
    <LibreAppWrapper app={inboundApp} appType="inbound">
      <div id="inbound-partner" className="d-flex flex-column">
        <span id="inboundConnectAccount-text" className="text-xl line-height-md mb-4">
          {i18n.t('Partners.Inbound.connectYourAccount', { partnerName: partner.name })}
        </span>

        <span id="inboundUploadInfo-text" className="text-soft mb-4">
          {i18n.t('Partners.Inbound.uploadInfo', { partnerName: partner.name })}
        </span>

        <span id="inboundContinueLogInto-text" className="text-soft mb-4">
          {i18n.t('Partners.Inbound.continueLogInto', { partnerName: partner.name })}
        </span>

        <div className="pt-4">
          <LoadingButton
            id="inboundConnectButton"
            className="btn btn-lg btn-primary w-100"
            onClick={() => {
              setRunning(true);
              connectInbound(partner);
            }}
            running={running}
          >
            {i18n.t('Partners.Inbound.connectWith', {
              partnerName: partner.name.toUpperCase(),
              context: 'cap',
            })}
          </LoadingButton>
        </div>
      </div>

      <button
        id="inboundCancelButton"
        className="btn btn-lg text-primary w-100 pt-4"
        onClick={cancel}
      >
        {i18n.t('Common.cancel', { context: 'cap' })}
      </button>
    </LibreAppWrapper>
  );
};

export default connector(InboundPartner);
